import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { authVerify } from '@/lib/real-api';
import type { Role, User } from '@/lib/types';
import { FIXTURES, fixtureRole, FIXTURE_USER, FIXTURE_WORKSPACE } from '@/lib/fixtures';

interface WorkspaceSummary {
  id: number;
  workspace_id: string;
  name: string;
  logo_url: string | null;
  role: Role;
  ticket_workflow: string;
}

interface AuthContextType {
  user: User | null;
  workspace: WorkspaceSummary | null;
  role: Role | null;
  loading: boolean;
  updateWorkspace: (updates: Partial<Pick<WorkspaceSummary, 'name' | 'logo_url'>>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthContextType, 'updateWorkspace'>>({
    user: null,
    workspace: null,
    role: null,
    loading: true,
  });

  useEffect(() => {
    if (FIXTURES) {
      const role = fixtureRole();
      setState({ user: FIXTURE_USER as unknown as User, workspace: { ...FIXTURE_WORKSPACE, role }, role, loading: false });
      return;
    }
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setState({ user: null, workspace: null, role: null, loading: false });
        return;
      }

      try {
        const idToken = await firebaseUser.getIdToken();
        const res = await authVerify(
          idToken,
          firebaseUser.uid,
          firebaseUser.email || '',
          firebaseUser.displayName || undefined,
          firebaseUser.photoURL || undefined,
        );

        const role = (res.workspace?.role as Role) ?? null;
        const workspace = res.workspace
          ? { id: res.workspace.id, workspace_id: res.workspace.workspace_id, name: res.workspace.name, logo_url: res.workspace.logo_url ?? null, role: role as Role, ticket_workflow: res.workspace.ticket_workflow ?? 'claim_based' }
          : null;

        setState({ user: res.user, workspace, role, loading: false });

        if (!res.workspace_exists) {
          window.location.href = '/create-workspace';
        }
      } catch {
        setState({ user: null, workspace: null, role: null, loading: false });
      }
    });
    return () => unsub();
  }, []);

  function updateWorkspaceInContext(updates: Partial<Pick<WorkspaceSummary, 'name' | 'logo_url'>>) {
    setState((prev) => ({
      ...prev,
      workspace: prev.workspace ? { ...prev.workspace, ...updates } : null,
    }));
  }

  return (
    <AuthContext.Provider value={{ ...state, updateWorkspace: updateWorkspaceInContext }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
